import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@folio/components';
import { MAX_FLEET_SIZE } from '@folio/constants';
import { formatPrice } from '@folio/lib';
import { formatDate } from '@folio/lib/date-formatting';
import {
  claimsListQueryOptions,
  getClaimsCategoryLabel,
  getClaimsReferenceDate,
  groupClaimsByCategory,
  isHistoricalClaim,
} from '@folio/utils/claims';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { useQueries } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import {
  type AssetDto,
  type ClaimDto,
  type KeyValueFieldObject,
  type PolicyDto,
  keyValueFieldsToObject,
  viewSpecificControllerListPolicyAssetsQueryOptions,
} from 'korint-sdk';
import {
  type ClaimsReviewConfig,
  type ClaimsReviewSection,
  useKorintConfig,
} from 'korint-sdk/react';
import { Trans, useTranslation } from 'react-i18next';
import { selectVisibleClaimsSections } from './selectVisibleClaimsSections';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text, Title } from '@folio/components/ui/text';

type ComparedPolicy = Omit<PolicyDto, 'customFields'> & { customFields: KeyValueFieldObject };

type ClaimsReviewModuleProps = {
  config: ClaimsReviewConfig;
  /** Compared branches, the reviewed one last; undefined is the base branch */
  branches: Array<string | undefined>;
  /** Policy on each compared branch, in the same order as `branches` */
  policies: ComparedPolicy[];
};

type QueryStatus = 'pending' | 'error' | 'success';

export function ClaimsReviewModule({ config, branches, policies }: ClaimsReviewModuleProps) {
  const { t } = useTranslation();
  const reviewedPolicy = policies[policies.length - 1];
  const referenceDate = getClaimsReferenceDate(reviewedPolicy.startedAt);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('claimsReview.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {config.attachedTo === 'policy' ? (
          <PolicyClaimsReview
            sections={config.sections}
            policies={policies}
            productId={reviewedPolicy.productId}
            referenceDate={referenceDate}
          />
        ) : (
          <AssetClaimsReview
            branches={branches}
            policyId={reviewedPolicy.id}
            productId={reviewedPolicy.productId}
            referenceDate={referenceDate}
          />
        )}
      </CardContent>
    </Card>
  );
}

function PolicyClaimsReview({
  sections,
  policies,
  productId,
  referenceDate,
}: {
  sections?: ClaimsReviewSection[];
  policies: ComparedPolicy[];
  productId: string;
  referenceDate: Dayjs;
}) {
  const { t } = useTranslation();
  const policyId = policies[0].id;
  const visibleSections = selectVisibleClaimsSections(sections, policies);

  const claimsQueries = useQueries({
    queries: visibleSections.map(section => ({
      ...claimsListQueryOptions(policyId, section.claimsMetadata),
      select: (data: { data: ClaimDto[] }) => data.data,
    })),
  });

  return visibleSections.map((section, index) => (
    <div key={section.id ?? 'all'}>
      {section.id && (
        <Title order={3} className="text-md pb-2 font-medium">
          {t(`${productId}.claimsReview.sections.${section.id}`)}
        </Title>
      )}
      <ClaimsReviewList
        status={claimsQueries[index].status}
        claims={claimsQueries[index].data ?? []}
        productId={productId}
        referenceDate={referenceDate}
      />
    </div>
  ));
}

function AssetClaimsReview({
  branches,
  policyId,
  productId,
  referenceDate,
}: {
  branches: Array<string | undefined>;
  policyId: string;
  productId: string;
  referenceDate: Dayjs;
}) {
  const { korintConfig } = useKorintConfig();
  const assetClaimsMetadata = korintConfig.modulesConfig?.assetClaims?.claimsMetadata;

  const assetsQueries = useQueries({
    queries: branches.map(branchId =>
      viewSpecificControllerListPolicyAssetsQueryOptions(policyId, {
        branchId,
        count: MAX_FLEET_SIZE,
      }),
    ),
  });

  // The reviewed branch comes last, so its version of an asset wins
  const assetsById = Object.fromEntries(
    assetsQueries.flatMap(query => query.data?.data ?? []).map(asset => [asset.id, asset]),
  );

  const claimsQueries = useQueries({
    queries: Object.keys(assetsById).map(assetId => ({
      ...claimsListQueryOptions(assetId, assetClaimsMetadata),
      select: (data: { data: ClaimDto[] }) => data.data,
    })),
  });

  const queries = [...assetsQueries, ...claimsQueries];
  const status: QueryStatus = queries.some(query => query.isError)
    ? 'error'
    : queries.some(query => query.isPending)
      ? 'pending'
      : 'success';

  return (
    <ClaimsReviewList
      status={status}
      claims={claimsQueries.flatMap(query => query.data ?? [])}
      productId={productId}
      referenceDate={referenceDate}
      assetsById={assetsById}
    />
  );
}

function ClaimsReviewList({
  status,
  claims,
  productId,
  referenceDate,
  assetsById,
}: {
  status: QueryStatus;
  claims: ClaimDto[];
  productId: string;
  referenceDate: Dayjs;
  assetsById?: Record<string, AssetDto>;
}) {
  const { t } = useTranslation();
  const claimsConfig = usePolicyProductConfig('claims');

  if (status === 'pending') {
    return (
      <Stack gap={2}>
        <Skeleton className="h-[40px] w-full" />
        <Skeleton className="h-[40px] w-full" />
      </Stack>
    );
  }

  if (status === 'error') {
    return <div className="text-sm text-destructive">{t('policyClaims.fetchError')}</div>;
  }

  const groups = groupClaimsByCategory(claims, claimsConfig.claimsCategories).filter(
    group => group.claims.length > 0,
  );

  if (groups.length === 0) {
    return <div className="text-sm text-muted-foreground">{t('claimsReview.empty')}</div>;
  }

  return groups.map(group => (
    <div key={group.category}>
      <Title order={4} size="sm" className="py-2 font-medium">
        {getClaimsCategoryLabel(productId, group, t)}
      </Title>
      <div className="divide-y border rounded-md">
        {group.claims.map(claim => {
          const customFields = keyValueFieldsToObject(claim.customFields ?? []);
          const asset = assetsById && claim.externalId ? assetsById[claim.externalId] : undefined;

          return (
            <Group
              key={claim.id}
              data-testid="claims-review-item"
              wrap
              gap={0}
              className="p-4 gap-x-8 gap-y-2 text-sm"
            >
              {assetsById && (
                <Text inline weight="medium">
                  {asset?.name ?? t('assetClaims.assetFallbackName')}
                </Text>
              )}
              <ClaimDetail
                label={t('policyClaims.occurredAt')}
                value={claim.occurredAt ? formatDate(new Date(claim.occurredAt)) : '-'}
              />
              {group.hasResponsibility && (
                <ClaimDetail
                  label={t('policyClaims.responsibility')}
                  value={
                    claim.responsibility ? t(`claimResponsibility.${claim.responsibility}`) : '-'
                  }
                />
              )}
              {claim.reserve !== undefined && (
                <span>{t('policyClaims.reserve', { amount: formatPrice(claim.reserve) })}</span>
              )}
              {claimsConfig.fieldsConfig?.map(field => (
                <ClaimDetail
                  key={field.name}
                  label={t(
                    [`fields.${field.name}.informationField.label`, `fields.${field.name}.label`],
                    { defaultValue: field.name },
                  )}
                  value={String(customFields[field.name] ?? '-')}
                />
              ))}
              {isHistoricalClaim(claim, referenceDate) && (
                <Text
                  inline
                  size="xs"
                  weight="semibold"
                  className="bg-slate-200 text-amber-600 px-4 py-1 rounded-full uppercase"
                >
                  {t('policyClaims.historical')}
                </Text>
              )}
            </Group>
          );
        })}
      </div>
    </div>
  ));
}

function ClaimDetail({ label, value }: { label: string; value: string }) {
  return (
    <span>
      <Trans
        i18nKey="claimsReview.detail"
        values={{ label, value }}
        components={{ label: <Text inline tone="muted" /> }}
      />
    </span>
  );
}
